import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n, pathFor } from "@/i18n/I18nProvider";
import { Menu, X, ShieldCheck } from "lucide-react";

const navKeys = [
  { key: "programs", hash: "#programs" },
  { key: "literacy", hash: "#literacy" },
  { key: "contact", hash: "#newsletter" },
];

export default function Header() {
  const { lang, t, switchLang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const homePath = pathFor(lang, "");

  const buildLink = (hash) => `${homePath}${hash}`;
  const isHome = location.pathname === homePath || location.pathname === `${homePath}/`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-slate-800 bg-[#0A2540] text-white"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to={homePath}
          data-testid="header-logo-link"
          className="flex items-center gap-2.5"
          onClick={() => setOpen(false)}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-500/15 ring-1 ring-emerald-400/40">
            <ShieldCheck className="h-5 w-5 text-emerald-300" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-base font-bold tracking-tight">{t("brand.name")}</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-300">
              {t("brand.tagline")}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" data-testid="header-nav-desktop">
          {navKeys.map((n) => (
            <a
              key={n.key}
              href={buildLink(n.hash)}
              data-testid={`nav-link-${n.key}`}
              className="rounded-md px-3 py-2 text-sm font-medium text-slate-100 hover:bg-white/5 hover:text-white"
            >
              {t(`nav.${n.key}`)}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div
            className="flex items-center overflow-hidden rounded-md ring-1 ring-white/15"
            role="group"
            aria-label={t("nav.language")}
          >
            <button
              type="button"
              onClick={() => switchLang("mx")}
              data-testid="language-switcher-es"
              className={`px-3 py-1.5 text-xs font-semibold ${
                lang === "mx" ? "bg-emerald-500 text-slate-900" : "text-slate-100 hover:bg-white/5"
              }`}
              aria-pressed={lang === "mx"}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => switchLang("en")}
              data-testid="language-switcher-en"
              className={`px-3 py-1.5 text-xs font-semibold ${
                lang === "en" ? "bg-emerald-500 text-slate-900" : "text-slate-100 hover:bg-white/5"
              }`}
              aria-pressed={lang === "en"}
            >
              EN
            </button>
          </div>
          <a
            href={buildLink("#newsletter")}
            data-testid="header-cta-button"
            className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400"
          >
            {t("hero.primaryCta")}
          </a>
        </div>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md ring-1 ring-white/20 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#0A2540] lg:hidden" data-testid="mobile-nav-panel">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6">
            {navKeys.map((n) => (
              <a
                key={n.key}
                href={buildLink(n.hash)}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.key}`}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-slate-100 hover:bg-white/5"
              >
                {t(`nav.${n.key}`)}
              </a>
            ))}
            <div className="mt-2 flex items-center justify-between border-t border-white/10 pt-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { switchLang("mx"); setOpen(false); }}
                  data-testid="mobile-language-switcher-es"
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ring-1 ring-white/15 ${
                    lang === "mx" ? "bg-emerald-500 text-slate-900" : "text-slate-100"
                  }`}
                >
                  ES
                </button>
                <button
                  type="button"
                  onClick={() => { switchLang("en"); setOpen(false); }}
                  data-testid="mobile-language-switcher-en"
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ring-1 ring-white/15 ${
                    lang === "en" ? "bg-emerald-500 text-slate-900" : "text-slate-100"
                  }`}
                >
                  EN
                </button>
              </div>
              <a
                href={buildLink("#newsletter")}
                onClick={() => setOpen(false)}
                data-testid="mobile-header-cta"
                className="rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-slate-900"
              >
                {t("hero.primaryCta")}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
